<template>
  <div
    class="flex flex-row justify-between px-2 py-0.5 lg:py-3 lg:pl-5 lg:pr-3 gap-2 bg-gradient-gray-neutral border-b border-secondary items-center"
  >
    <div class="flex flex-row gap-2 items-center truncate">
      <VisibilityToggle
        :state="!file.public"
        :readonly="!canUpdate"
        @update="updateVisbility"
      />
      <div v-if="maxTablet" class="flex gap-2 items-center truncate">
        <Divider orientation="vertical" class="h-8.5" />
        <div class="text-quaternary text-style-sm font-medium truncate">
          {{ file.tenant.name }}
        </div>
      </div>
    </div>
    <div v-if="maxSmall" class="flex flex-row gap-1 items-center">
      <ActionsModal
        :file="file"
        @delete="handleDelete()"
        @download="handleDownload"
      >
        <IconButton variant="close">
          <Icon name="dots-horizontal" size="xl" />
        </IconButton>
      </ActionsModal>
      <DialogClose
        :as="CloseButton"
        size="lg"
        :aria-label="$t('components.buttons.close')"
      />
    </div>
    <div v-else class="flex flex-row gap-2">
      <div class="flex flex-row gap-2">
        <DeleteModal
          :id="file.id"
          :title="$t('components.file_details.delete.title')"
          :description="$t('components.file_details.delete.description')"
          @delete="handleDelete()"
        >
          <Button
            v-if="canDelete"
            variant="tertiary"
            size="md"
            type="gray"
            icon
          >
            <Icon name="delete" size="lg" />
          </Button>
        </DeleteModal>
        <Button
          variant="tertiary"
          size="md"
          type="gray"
          icon
          as="a"
          :href="download"
          target="_blank"
        >
          <Icon name="download" size="lg" />
        </Button>
        <MediaViewer
          v-if="isSupportedMedia(file.file.mime_type)"
          :title="file.file.name"
          :src="file.file.urls.file"
          :type="file.file.mime_type"
        >
          <Button
            variant="primary"
            size="md"
            type="filled"
            :label="$t('components.buttons.preview')"
          >
            <template #start>
              <Icon name="eye-visible" />
            </template>
          </Button>
        </MediaViewer>
      </div>
    </div>
  </div>
</template>
<script setup>
// Import Vue Utilities
import { computed, inject } from "vue";

// Import Utilities
import axios from "axios";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { router } from "@inertiajs/vue3";

// Import Components
import { ActionsModal, VisibilityToggle } from "@/Pages/Files/Partials/Detail";
import { Button, CloseButton, IconButton } from "@/SharedComponents/Buttons";
import { DialogClose } from "@/SharedComponents/Dialog";
import { Divider } from "@/SharedComponents/Divider";
import { Icon } from "@/SharedComponents/Icon";
import { MediaViewer, isSupportedMedia } from "@/SharedComponents/Media";
import { DeleteModal } from "@/SharedComponents/Modals";
import { toast } from "@/SharedComponents/Toaster";

// Import Composables
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useWebView } from "@/Composables/useWebView";
import { mobileSatisfiesVersion } from "@/Utils/version";

// Emitter
const emitter = inject("emitter");

// Breakpoints
const { maxTablet, maxSmall } = useBreakpoints();

// Translations
const { t } = useI18n();

// Define Props
const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  canUpdate: {
    type: Boolean,
    required: true,
  },
});

const { runningInWebView, openURL } = useWebView();

const handleDownload = (url) => {
  if (runningInWebView() && mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  window.open(url, "_blank");
};

// Computed Props
const download = computed(() => props.file.file.urls.download);

// Functions
const updateVisbility = () => {
  if (!props.canUpdate) {
    return;
  }

  axios
    .post(route("api.files.visibility"), {
      note_ids: [props.file.id],
      is_private: props.file.public,
    })
    .then((response) => {
      // Show the succes toast
      toast.success(t("toasts.files.update.success"));

      // Update files
      emitter.emit("update-files-data", response.data);

      // Reload the router
      router.reload();
    })
    .catch((error) => {
      if (error.response.status == 403) {
        toast.error(t("toasts.files.update.403"));
      } else {
        toast.error(
          error.response.data.message || t("toasts.files.update.error")
        );
      }
    });
};

const handleDelete = () => {
  axios
    .delete(route("api.files.destroy"), {
      data: {
        note_ids: [props.file.id],
      },
    })
    .then(() => {
      // Show success toast
      toast.success(t("toasts.files.delete.success"));

      // Remove file in data
      emitter.emit("remove-files-data", { data: [props.file.id] });

      // Navigate to files
      router.visit(route("files.index"), {
        preserveState: true,
        preserveScroll: true,
      });
    })
    .catch((error) => {
      if (error.response.status == 403) {
        toast.error(t("toasts.files.delete.403"));
      } else {
        toast.error(t("toasts.files.delete.error"));
      }
    });
};
</script>
